import { useEffect } from 'react';
import { useRecoilValue } from 'recoil';
import { CampaignType } from 'services/campaign/types';
import { organizationAtom } from 'services/organization/atom';
import { userAtom } from 'services/user/atoms';
import { getUserContactEmail } from 'services/user/utils/getUserContactEmail';
import { FormValues } from '../types';
import { Channels, OpeningMessageMode } from 'generated-graphql/types';
import { getDefaultTemplatedMessage } from '../utils/getDefaultTemplatedMessage';
import { getDefaultSubjectLine } from '../utils/getDefaultSubjectLine';
import { getDefaultRejectionMessage } from 'modules/campaign-creation/utils/getDefaultRejectionMessage';
import { getDefaultClosingMessage } from 'modules/campaign-creation/utils/getDefaultClosingMessage';
import { OrganizationConfig } from 'services/organization/type';

type Params = {
  setFieldValue: (field: string, value: string) => void;
  values: FormValues;
  organizationConfig: OrganizationConfig | null;
};

export const useDefaultFieldsSetter = ({
  setFieldValue,
  values,
  organizationConfig
}: Params) => {
  const user = useRecoilValue(userAtom);
  const organization = useRecoilValue(organizationAtom);

  const { type, customTemplatedMessage, channel } = values;

  useEffect(() => {
    // Set default fields values
    if (user?.email) {
      const email = getUserContactEmail(user);

      const closingMessage = getDefaultClosingMessage({
        organizationConfig,
        savedClosingMessage: values.closingMessage,
        userEmail: email
      });

      setFieldValue('closingMessage', closingMessage);
    }

    if (organization?.websiteUrl && user?.email) {
      const { websiteUrl } = organization;
      const email = getUserContactEmail(user);

      const rejectionMessage = getDefaultRejectionMessage({
        isEngagementOutreach: type === CampaignType.ENGAGEMENT_OUTREACH,
        organizationConfig,
        savedRejectionMessage: values.rejectionMessage,
        userEmail: email,
        websiteUrl
      });

      setFieldValue('rejectionMessage', rejectionMessage);
    }

    if (
      channel === Channels.Sms &&
      !customTemplatedMessage &&
      (organizationConfig?.openingMessageMode ===
        OpeningMessageMode.CustomOpeningMessage ||
        organizationConfig?.openingMessageMode ===
          OpeningMessageMode.CustomOrTemplatedOpeningMessage)
    ) {
      const customTemplatedMessage = getDefaultTemplatedMessage({
        campaignType: type,
        channel
      });

      if (customTemplatedMessage) {
        setFieldValue('customTemplatedMessage', customTemplatedMessage);
      }
    }

    if (channel === Channels.Email) {
      const customTemplatedMessage = getDefaultTemplatedMessage({
        campaignType: type,
        channel
      });

      if (customTemplatedMessage) {
        setFieldValue('customTemplatedMessage', customTemplatedMessage);
      }

      const customTemplatedSubjectLine = getDefaultSubjectLine({
        campaignType: type
      });

      if (customTemplatedSubjectLine) {
        setFieldValue('customTemplatedSubjectLine', customTemplatedSubjectLine);
      }
    }
  }, [
    user?.id,
    organization?.websiteUrl,
    type,
    channel,
    organizationConfig?.organizationId
  ]);
};
